import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, fromEvent, Observable } from 'rxjs';
import * as _ from 'lodash';
import { WINDOW } from '../tokens/window';
import { BROWSER_LOCALE } from '../utils/constants';
import { Locale } from './localization.service';

const LOCAL_STORAGE_KEY = 'userConfigCfgUi';

export interface UiConfigPersonalization {
  // Maps
  areasLayerTimeout: number;
  infrastructureLayerTimeout: number;
  tagsLayerTimeout: number;
  settingsPanelRefreshTimeout: number;
  sensorsRefreshTimeout: number;
  // Application wide personalization
  locale: Locale | typeof BROWSER_LOCALE;
  notificationsRefresh: number;
  notificationsShowPopup: boolean;
  toastHide: number;
  sortMapsByHeight: boolean;
  uiLinkTimeBeforeEvent: number;
  uiLinkTimeAfterEvent: number;
}

export interface UiConfig {
  personalization: UiConfigPersonalization;
  version: string;
}

export const UI_CONFIG_VERSION = '1.1';
export const UI_CONFIG_DEFAULTS: UiConfig = {
  personalization: {
    areasLayerTimeout: 10000,
    infrastructureLayerTimeout: 5000,
    tagsLayerTimeout: 1000,
    settingsPanelRefreshTimeout: 5000,
    sensorsRefreshTimeout: 1000,
    sortMapsByHeight: false,
    toastHide: 10000,
    notificationsRefresh: 15000,
    notificationsShowPopup: true,
    locale: BROWSER_LOCALE,
    uiLinkTimeBeforeEvent: 60,
    uiLinkTimeAfterEvent: 300,
  },
  version: UI_CONFIG_VERSION,
};

@Injectable({
  providedIn: 'root',
})
export class UiConfigService {
  private uiConfig: UiConfig | null;

  private uiConfigSerialized: string | null;

  private uiConfigSubject: BehaviorSubject<UiConfig | null>;

  /**
   * UiConfig currently can't be null. But in case of future changes
   * when UiConfig can be pulled from a server we should make all consumers
   * to be ready for the change when it's implemented.
   * Thus type for public access to config is set to "UiConfig | null"
   */
  readonly uiConfig$: Observable<UiConfig | null>;

  constructor(@Inject(WINDOW) private window: Window) {
    this.uiConfigSerialized = this.window.localStorage.getItem(LOCAL_STORAGE_KEY);
    this.uiConfig = this.deserializeConfig(this.uiConfigSerialized);
    this.uiConfigSubject = new BehaviorSubject<UiConfig | null>(this.uiConfig);
    this.uiConfig$ = this.uiConfigSubject.asObservable();

    fromEvent<StorageEvent>(this.window, 'storage').subscribe((event) => {
      if (event.key === LOCAL_STORAGE_KEY && event.newValue !== this.uiConfigSerialized) {
        this.uiConfigSerialized = event.newValue;
        this.uiConfig = this.deserializeConfig(this.uiConfigSerialized);
        this.uiConfigSubject.next(this.uiConfig);
      }
    });
  }

  private deserializeConfig(uiConfigSerialized: string | null): UiConfig {
    let uiConfig: UiConfig = UI_CONFIG_DEFAULTS;
    if (uiConfigSerialized != null) {
      uiConfig = JSON.parse(uiConfigSerialized);
    }
    if (uiConfig.version !== UI_CONFIG_VERSION) {
      uiConfig = UI_CONFIG_DEFAULTS;
    }
    return uiConfig;
  }

  setUiConfig(uiConfig: UiConfig | null): void {
    const uiConfigSerialized = JSON.stringify(uiConfig);
    if (this.uiConfigSerialized !== uiConfigSerialized) {
      this.uiConfigSerialized = uiConfigSerialized;
      if (uiConfigSerialized != null) {
        this.window.localStorage.setItem(LOCAL_STORAGE_KEY, uiConfigSerialized);
      } else {
        this.window.localStorage.removeItem(LOCAL_STORAGE_KEY);
      }
      this.uiConfig = _.cloneDeep(uiConfig);
      this.uiConfigSubject.next(this.uiConfig);
    }
  }
}
